/**
 * Button styling tokens, beside the design system's CSS and shared by both
 * frontends (R22 T10).
 *
 * A plain module with no framework in it, so a server component, a client
 * component and the static web app can all compose the same button classes.
 * It moved out of the Next app's components because that host retired at T18
 * and these classes do not: they are the design system expressed in TypeScript,
 * and the one thing worse than a token defined in two places is a *button*
 * defined in two places.
 */

export const BUTTON_BASE =
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-4 py-2.5 font-semibold outline-none transition duration-150 ease-out focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-60";

export const BUTTON_VARIANT = {
  primary: "bg-primary text-on-primary hover:brightness-110 active:brightness-95",
  secondary:
    "border border-line bg-surface text-ink hover:bg-page active:brightness-95",
  ghost: "bg-transparent text-accent hover:bg-accent/10 active:bg-accent/15",
  danger: "bg-danger text-white hover:brightness-95 active:brightness-90",
  /**
   * The primary action **on the brand band**, where `primary` cannot be used:
   * the band and the primary fill are both `#06183d`, so a primary button on
   * the hero was navy on navy, and rendered as floating white text with no
   * button around it at all.
   *
   * Teal is the only other colour the brand owns, and it is already what the
   * band uses for emphasis, so the fix stays inside the palette rather than
   * introducing a fifth hue. Navy text on teal, not white: teal is light
   * enough that white on it is the weaker pairing.
   */
  onBrand: "bg-teal text-ink hover:brightness-105 active:brightness-95",
} as const;

export type ButtonVariant = keyof typeof BUTTON_VARIANT;

/**
 * Button styling for elements that aren't <button>, e.g. a router <Link> that
 * should read as a primary action. Keeps links and buttons visually identical.
 */
export function buttonClass(variant: ButtonVariant = "primary", className = "") {
  return `${BUTTON_BASE} ${BUTTON_VARIANT[variant]} ${className}`.trim();
}
